/**
 * BeneLoginGate — the login half of Customer Gate 2.0.
 *
 * Wraps the customer_login form on templates/customers/login.liquid and behaves
 * differently depending on which store is serving it:
 *
 * data-mode="ch"  The password field starts hidden (CSS, off data-step, so
 *                 there is no flash). The form asks for the email first, then
 *                 asks beneganic.com whether that email is already a B2B
 *                 customer there:
 *                   yes -> hand the customer over to .com/account/login?email=
 *                   no  -> reveal the password and log them in here on .ch
 *                 A failed lookup reveals the password too: a broken worker
 *                 must not lock anybody out of the Swiss store.
 *
 * data-mode="com" The other end of that handoff. Prefills the email from the
 *                 ?email= parameter and puts the cursor in the password field.
 *
 * data-mode="off" Does nothing at all: any store that is neither .com nor .ch.
 *
 * With JavaScript disabled the <noscript> block in the template reveals the
 * password field and the form stays the ordinary Shopify login it has always
 * been.
 *
 * No submit event is allowed to fire while the email step is open. Shopify's
 * bot protection arms its hCaptcha challenge on the form's submit event, and
 * calling preventDefault() does not un-arm it: the challenge appears anyway,
 * on a step that is not a login attempt. So the button is switched to
 * type="button" and Enter is intercepted on the email field, which means the
 * lookup runs without the form ever being submitted. The submit listener that
 * remains is a capture-phase backstop; it stops propagation so that no other
 * submit listener runs either.
 */
class BeneLoginGate extends HTMLElement {
  /* Shopify does not hand the submitted email back on a failed login, so it is
     stashed here to survive the round trip and the customer does not have to
     type it twice. Per-tab, cleared as soon as it is used. */
  static get STASH_KEY() {
    return 'bene-login-gate:email';
  }

  connectedCallback() {
    this.mode = this.getAttribute('data-mode') || 'off';
    if (this.mode === 'off') return;

    this.form = this.closest('form');
    this.emailInput = this.querySelector('[data-login-gate-email]');
    this.passwordInput = this.querySelector('[data-login-gate-password-input]');
    this.passwordFields = Array.from(this.querySelectorAll('[data-login-gate-password]'));
    this.submitButton = this.querySelector('[data-login-gate-submit]');
    this.message = this.querySelector('[data-login-gate-message]');

    if (!this.form || !this.emailInput) return;

    if (this.mode === 'com') {
      this.prefillFromQuery();
      return;
    }

    this.lookupConfig = {
      endpoint: this.getAttribute('data-lookup-endpoint') || '',
    };
    this.comLoginUrl = this.getAttribute('data-com-login-url') || 'https://beneganic.com/account/login';
    this.signInLabel = (this.submitButton && this.submitButton.textContent.trim()) || '';
    this.continueLabel = this.getAttribute('data-continue-label') || this.signInLabel;

    this.onSubmit = this.onSubmit.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onEmailKeydown = this.onEmailKeydown.bind(this);

    /* Capture, so this runs before any submit listener Shopify's bot
       protection may have registered on the form. */
    this.form.addEventListener('submit', this.onSubmit, true);
    if (this.submitButton) this.submitButton.addEventListener('click', this.onButtonClick);
    this.emailInput.addEventListener('keydown', this.onEmailKeydown);

    /* A failed login means the customer has already been through the lookup
       and is on .ch for good: go straight to the password step. */
    if (this.getAttribute('data-has-errors') === 'true') {
      this.restoreStashedEmail();
      /* Shopify only rejects a password once the lookup has already sent this
         customer here, so the address counts as cleared and the next attempt
         goes straight through instead of asking .com all over again. */
      this.checkedEmail = this.email;
      this.openPasswordStep({ focus: false, announce: false });
    } else {
      this.closePasswordStep();
    }
  }

  disconnectedCallback() {
    if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
    if (this.submitButton && this.onButtonClick) {
      this.submitButton.removeEventListener('click', this.onButtonClick);
    }
    if (this.emailInput && this.onEmailKeydown) {
      this.emailInput.removeEventListener('keydown', this.onEmailKeydown);
    }
  }

  /* ------------------------------------------------------------------ .com */

  prefillFromQuery() {
    var email = '';
    try {
      email = new URLSearchParams(window.location.search).get('email') || '';
    } catch (error) {
      return;
    }
    if (!email.trim()) return;

    this.emailInput.value = email.trim();
    /* The floating label keys off :not(:placeholder-shown), so it lifts on its
       own — but other listeners on the form still want to know. */
    this.emailInput.dispatchEvent(new Event('input', { bubbles: true }));

    if (this.passwordInput) this.passwordInput.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------- .ch */

  get email() {
    return this.emailInput.value.trim().toLowerCase();
  }

  closePasswordStep() {
    this.setAttribute('data-step', 'email');
    /* Out of the tab order and out of the submitted payload while hidden. */
    if (this.passwordInput) this.passwordInput.disabled = true;
    if (this.submitButton) {
      /* The one thing that keeps the captcha out of the email step: a
         type="button" click cannot submit the form, so no submit event exists
         for the bot protection to hook. Set from JS only, so the <noscript>
         form keeps its ordinary submit button. */
      this.submitButton.type = 'button';
      this.setButtonLabel(this.continueLabel);
    }
  }

  openPasswordStep(options) {
    options = options || {};
    this.setAttribute('data-step', 'password');
    if (this.passwordInput) {
      this.passwordInput.disabled = false;
      if (options.focus !== false) this.passwordInput.focus({ preventScroll: true });
    }
    if (this.submitButton) {
      /* A real login attempt from here on, captcha and all. */
      this.submitButton.type = 'submit';
      this.setButtonLabel(this.signInLabel);
    }
    this.showMessage(options.announce === false ? '' : this.getAttribute('data-password-hint'));
  }

  setButtonLabel(label) {
    if (!label) return;
    this.submitButton.textContent = label;
    this.submitButton.setAttribute('aria-label', label);
  }

  showMessage(text, isError) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.hidden = !text;
    this.message.classList.toggle('login-gate__message--error', Boolean(isError));
  }

  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) this.setButtonLabel(this.getAttribute('data-checking-label'));
  }

  stashEmail() {
    try {
      window.sessionStorage.setItem(BeneLoginGate.STASH_KEY, this.emailInput.value);
    } catch (error) {
      /* Private mode, or storage disabled — the field just starts empty. */
    }
  }

  restoreStashedEmail() {
    if (this.emailInput.value.trim()) return;
    try {
      var stashed = window.sessionStorage.getItem(BeneLoginGate.STASH_KEY);
      window.sessionStorage.removeItem(BeneLoginGate.STASH_KEY);
      if (stashed) {
        this.emailInput.value = stashed;
        this.emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } catch (error) {
      /* As above. */
    }
  }

  /**
   * False once this exact address has been cleared on the password step, which
   * is the only state where the form may really be submitted. Changing the
   * email after the password appeared sends it back through the lookup, or a
   * B2B customer could slip past by editing the field.
   */
  needsLookup() {
    return !(this.getAttribute('data-step') === 'password' && this.email === this.checkedEmail);
  }

  /* The email step's own button. Never submits, so the captcha stays asleep. */
  onButtonClick(event) {
    if (!this.needsLookup()) return;
    event.preventDefault();
    this.attempt();
  }

  /* Enter in the email field would implicitly submit the form, captcha and
     all, even with the button switched to type="button". */
  onEmailKeydown(event) {
    if (event.key !== 'Enter' || event.isComposing) return;
    if (!this.needsLookup()) return;
    event.preventDefault();
    this.attempt();
  }

  /* Backstop for a submit we did not anticipate. Stops the event dead rather
     than merely cancelling it, so no other listener acts on it either. */
  onSubmit(event) {
    if (!this.needsLookup()) {
      this.stashEmail();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.attempt();
  }

  attempt() {
    var email = this.email;

    if (!email || !this.emailInput.checkValidity()) {
      this.showMessage(this.getAttribute('data-invalid-email-message'), true);
      this.emailInput.focus({ preventScroll: true });
      return;
    }

    this.check(email);
  }

  check(email) {
    if (typeof window.beneganicLookupComB2B !== 'function') {
      /* assets/bene-com-b2b-lookup.js never arrived. Same answer as a failed
         lookup: let the customer log in here. */
      console.warn('Login gate: the B2B lookup script is missing, continuing on .ch');
      this.checkedEmail = email;
      this.openPasswordStep();
      return;
    }

    this.setBusy(true);
    this.showMessage(this.getAttribute('data-checking-label'));

    window
      .beneganicLookupComB2B(email, this.lookupConfig)
      .then(
        function (result) {
          if (result.exists) {
            this.handOverToCom(email);
            return;
          }
          this.checkedEmail = email;
          this.setBusy(false);
          this.openPasswordStep();
          this.resubmitIfReady();
        }.bind(this)
      )
      .catch(
        function (error) {
          /* Fail open: the .ch login must keep working without the worker. */
          console.warn('Login gate: B2B lookup failed, continuing on .ch', error);
          this.checkedEmail = email;
          this.setBusy(false);
          this.openPasswordStep();
          this.resubmitIfReady();
        }.bind(this)
      );
  }

  /**
   * The lookup can happen with a password already typed — the customer edited
   * their email after the field appeared, or came back from a failed login. The
   * address is cleared by the time this runs, so send the form rather than make
   * them click a second time for no visible reason.
   */
  resubmitIfReady() {
    if (!this.passwordInput || !this.passwordInput.value) return;
    if (typeof this.form.requestSubmit !== 'function') return;
    /* requestSubmit() rejects an element that is not a submit button. */
    if (this.submitButton && this.submitButton.type === 'submit') {
      this.form.requestSubmit(this.submitButton);
    } else {
      this.form.requestSubmit();
    }
  }

  handOverToCom(email) {
    var target = this.comLoginUrl;
    target += (target.indexOf('?') === -1 ? '?' : '&') + 'email=' + encodeURIComponent(email);

    this.showMessage(this.getAttribute('data-handoff-message'));

    window.location.href = target;
  }
}

if (!customElements.get('bene-login-gate')) {
  customElements.define('bene-login-gate', BeneLoginGate);
}
